import { useState } from 'react';
import { allMovies, type RankingEntry, risingRanking, topRatedRanking, weeklyRanking } from '@/mocks/movies';
import SectionHeading from '@/pages/home/components/SectionHeading';

const tabs: { key: string; label: string; desc: string; data: RankingEntry[] }[] = [
  { key: 'weekly', label: '热映榜', desc: '看近期讨论热度，知道大家都在追什么', data: weeklyRanking },
  { key: 'top', label: '高分榜', desc: '长期沉淀的口碑佳作，适合慢慢品味', data: topRatedRanking },
  { key: 'rising', label: '飙升榜', desc: '涨幅最快的新片，抢先发现被低估的作品', data: risingRanking },
];

const trendMeta: Record<RankingEntry['trend'], { icon: string; className: string; label: string }> = {
  up: { icon: 'ri-arrow-up-fill', className: 'text-primary-600', label: '上升' },
  down: { icon: 'ri-arrow-down-fill', className: 'text-secondary-600', label: '下降' },
  same: { icon: 'ri-subtract-line', className: 'text-foreground-400', label: '持平' },
};

export default function RankingSection() {
  const [activeKey, setActiveKey] = useState(tabs[0].key);
  const activeTab = tabs.find((tab) => tab.key === activeKey) ?? tabs[0];
  const focusMovie = allMovies.find((movie) => movie.id === activeTab.data[0].movieId);

  return (
    <section id="ranking" className="w-full bg-background-100 py-14 md:py-20">
      <div className="mx-auto w-full max-w-[1200px] px-4 md:px-8">
        <SectionHeading
          eyebrow="排行榜"
          title="三个维度，找到属于你的那份榜单"
          subtitle="热映榜看讨论度，高分榜看沉淀口碑，飙升榜看新片涨幅。切换标签即可对比不同的选片视角。"
        />

        <div className="mt-8 inline-flex flex-wrap gap-1 rounded-full border border-background-200 bg-background-50 p-1">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setActiveKey(tab.key)}
              className={`whitespace-nowrap rounded-full px-4 py-2 font-label text-sm font-semibold transition-colors duration-200 ${
                activeKey === tab.key
                  ? 'bg-primary-600 text-background-50'
                  : 'text-foreground-600 hover:text-primary-600'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <p className="mt-3 font-label text-xs text-foreground-500 md:text-sm">{activeTab.desc}</p>

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
          <ol className="flex flex-col gap-2 lg:col-span-2">
            {activeTab.data.map((entry, index) => {
              const movie = allMovies.find((item) => item.id === entry.movieId);
              if (!movie) return null;
              const trend = trendMeta[entry.trend];
              return (
                <li
                  key={`${activeTab.key}-${entry.movieId}`}
                  className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors duration-200 md:gap-4 md:px-4 ${
                    index === 0
                      ? 'border-accent-300 bg-accent-100/60'
                      : 'border-background-200 bg-background-50 hover:border-primary-200'
                  }`}
                >
                  <span
                    className={`w-7 shrink-0 text-center font-heading text-lg font-black md:text-xl ${
                      index < 3 ? 'text-primary-600' : 'text-foreground-400'
                    }`}
                  >
                    {index + 1}
                  </span>
                  <div className="relative h-16 w-11 shrink-0 overflow-hidden rounded-md bg-background-200 md:h-[72px] md:w-12">
                    <img
                      src={movie.poster}
                      alt={`${movie.title} 海报`}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="truncate font-heading text-sm font-bold text-foreground-950 md:text-base">
                      {movie.title}
                    </h3>
                    <p className="mt-0.5 truncate font-label text-xs text-foreground-500">
                      {movie.genre} · {movie.year} · {movie.duration}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <span className="whitespace-nowrap rounded-md bg-secondary-100 px-2 py-0.5 font-label text-xs font-semibold text-secondary-900">
                      {entry.score}
                    </span>
                    <span className={`flex items-center gap-0.5 font-label text-xs ${trend.className}`}>
                      <i className={trend.icon} aria-hidden="true" />
                      {trend.label}
                    </span>
                  </div>
                </li>
              );
            })}
          </ol>

          {focusMovie ? (
            <aside className="flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 lg:col-span-1">
              <div className="flex items-center justify-between bg-primary-600 px-4 py-2.5">
                <span className="font-label text-sm font-bold text-background-50">本期焦点</span>
                <span className="inline-flex items-center gap-1 font-label text-xs text-accent-300">
                  <i className="ri-award-line" aria-hidden="true" />
                  {activeTab.label} No.1
                </span>
              </div>
              <div className="relative aspect-[16/10] w-full overflow-hidden bg-background-200">
                <img
                  src={focusMovie.poster}
                  alt={`${focusMovie.title} 海报`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
              <div className="flex flex-1 flex-col gap-3 p-4">
                <h3 className="font-heading text-lg font-black text-foreground-950">{focusMovie.title}</h3>
                <p className="font-label text-xs text-foreground-500">
                  导演 {focusMovie.director} · 主演 {focusMovie.cast}
                </p>
                <p className="text-sm leading-relaxed text-foreground-600">{focusMovie.blurb}</p>
                <div className="mt-auto flex flex-wrap gap-2 pt-1">
                  {focusMovie.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full bg-accent-100 px-3 py-1 font-label text-xs font-medium text-accent-900"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <div className="mt-1 flex items-center gap-2 border-t border-background-200 pt-3">
                  <i className="ri-star-fill text-accent-500" aria-hidden="true" />
                  <span className="font-heading text-lg font-black text-foreground-950">
                    {focusMovie.rating.toFixed(1)}
                  </span>
                  <span className="font-label text-xs text-foreground-500">综合评分</span>
                </div>
              </div>
            </aside>
          ) : null}
        </div>
      </div>
    </section>
  );
}